import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  Description as ClaimsIcon,
  People as ClientsIcon,
  Security as InsurersIcon,
  Badge as InsuranceRepIcon,
  Note as NotesIcon,
  AttachFile as AttachmentsIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/axios';

interface ExportResult {
  entity: string;
  success: boolean;
  message: string;
  downloadUrl?: string;
  recordCount?: number;
}

const ExportPage: React.FC = () => {
  const { user } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [results, setResults] = useState<ExportResult[]>([]);
  const [selectedEntities, setSelectedEntities] = useState<string[]>([]);

  const exportEntities = [
    {
      key: 'claims',
      name: 'Claims',
      icon: <ClaimsIcon />,
      description: 'Export all claims data to CSV',
    },
    {
      key: 'clients',
      name: 'Clients',
      icon: <ClientsIcon />,
      description: 'Export all client information to CSV',
    },
    {
      key: 'insurers',
      name: 'Insurers',
      icon: <InsurersIcon />,
      description: 'Export all insurer data to CSV',
    },
    {
      key: 'insurance-representatives',
      name: 'Insurance Representatives',
      icon: <InsuranceRepIcon />,
      description: 'Export all insurance representative data to CSV',
    },
    {
      key: 'notes',
      name: 'Notes',
      icon: <NotesIcon />,
      description: 'Export all notes data to CSV',
    },
    {
      key: 'attachments',
      name: 'Attachments',
      icon: <AttachmentsIcon />,
      description: 'Export all attachment metadata to CSV',
    },
  ];

  const handleEntityToggle = (entityKey: string) => {
    setSelectedEntities(prev => 
      prev.includes(entityKey) 
        ? prev.filter(key => key !== entityKey)
        : [...prev, entityKey]
    );
  };

  const handleExport = async () => {
    if (selectedEntities.length === 0) return;

    setExporting(true);
    setResults([]);
    
    const newResults: ExportResult[] = [];

    for (const entityKey of selectedEntities) {
      const entity = exportEntities.find(e => e.key === entityKey);
      if (!entity) continue;

      try {
        const response = await api.get(`/${entityKey}/export`, {
          responseType: 'blob',
        });

        // Create download link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${entityKey}_export_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        newResults.push({
          entity: entity.name,
          success: true,
          message: 'Export completed successfully',
          downloadUrl: url,
        });
      } catch (error: any) {
        newResults.push({
          entity: entity.name,
          success: false,
          message: error.response?.data?.message || 'Export failed'
        });
      }
    }

    setResults(newResults);
    setExporting(false);
  };

  const handleExportAll = async () => {
    setSelectedEntities(exportEntities.map(e => e.key));
    // Small delay to ensure state is updated
    setTimeout(() => handleExport(), 100);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Data Export
      </Typography>
      
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Export your data to CSV format for backup, analysis, or migration purposes.
          Select the entities you want to export and click "Export Selected" or "Export All".
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Select Entities to Export
                </Typography>
                <Button
                  variant="outlined"
                  onClick={handleExportAll}
                  disabled={exporting}
                  startIcon={<FileDownloadIcon />}
                >
                  Export All
                </Button>
              </Box>
              
              <List>
                {exportEntities.map((entity, index) => (
                  <React.Fragment key={entity.key}>
                    <ListItem>
                      <ListItemIcon>
                        {entity.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={entity.name}
                        secondary={entity.description}
                      />
                      <Button
                        variant={selectedEntities.includes(entity.key) ? "contained" : "outlined"}
                        onClick={() => handleEntityToggle(entity.key)}
                        disabled={exporting}
                        startIcon={selectedEntities.includes(entity.key) ? <SuccessIcon /> : <FileDownloadIcon />}
                      >
                        {selectedEntities.includes(entity.key) ? 'Selected' : 'Select'}
                      </Button>
                    </ListItem>
                    {index < exportEntities.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>

              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  onClick={handleExport}
                  disabled={selectedEntities.length === 0 || exporting}
                  startIcon={exporting ? <CircularProgress size={20} /> : <FileDownloadIcon />}
                >
                  {exporting ? 'Exporting...' : `Export Selected (${selectedEntities.length})`}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Export Results
              </Typography>
              
              {results.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No export results yet. Select entities and click "Export Selected" to begin.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {results.map((result, index) => (
                    <Paper key={index} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        {result.success ? (
                          <SuccessIcon color="success" />
                        ) : (
                          <ErrorIcon color="error" />
                        )}
                        <Typography variant="subtitle2">
                          {result.entity}
                        </Typography>
                        {result.success && (
                          <Chip 
                            label="Downloaded" 
                            size="small" 
                            color="success" 
                            icon={<DownloadIcon />}
                          />
                        )}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {result.message}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ExportPage;
